import { z } from 'zod';
import { DeliveryView } from './notifications';

/* ─────────── Надёжность и состояние системы (раздел 6.24: АД-02–06, АД-11, АД-12; уведомления 6.20: УВ-09) ─────────── */

/** Что проверяет портал или сообщают скрипты на серверах (infra/kz) */
export const OPS_CHECKS = {
  smtp: 'Почтовый сервер',
  telegram: 'Бот Telegram',
  telegram_webhook: 'Webhook Telegram',
  worker: 'Фоновые задачи (worker)',
  queues: 'Очереди задач',
  outbox: 'Очередь отправки сообщений',
  replica: 'Реплика базы во втором дата-центре',
  backup_db: 'Копия базы (раз в сутки)',
  backup_files: 'Копия файлов (раз в сутки)',
  backup_offsite: 'Полная копия вне сети (раз в неделю)',
  restore_check: 'Проверка восстановления (раз в неделю)',
  encryption: 'Шифрование дисков, базы и файлов',
  // Журнал изменений и целостность базы (ДС-11, НФ-12): пишет worker раз в сутки
  db_integrity: 'Проверка целостности базы',
  audit_seal: 'Печать журнала изменений',
} as const;
export type OpsCheckKey = keyof typeof OPS_CHECKS;
export const OpsCheckKey = z.enum(Object.keys(OPS_CHECKS) as [OpsCheckKey, ...OpsCheckKey[]]);

/** Проверки, о которых сообщают скрипты на серверах заказчика (реплику портал измеряет сам) */
export const REPORTED_CHECKS = [
  'backup_db',
  'backup_files',
  'backup_offsite',
  'restore_check',
  'encryption',
] as const satisfies readonly OpsCheckKey[];
export const ReportedCheckKey = z.enum(REPORTED_CHECKS);

/** Через сколько часов без свежего отчёта проверка считается просроченной (АД-03, АД-04: копия в сутки, проверка в неделю) */
export const REPORT_MAX_AGE_HOURS: Partial<Record<OpsCheckKey, number>> = {
  backup_db: 26,
  backup_files: 26,
  backup_offsite: 8 * 24,
  restore_check: 8 * 24,
  encryption: 8 * 24,
};

export const OpsStatus = z.enum(['ok', 'warn', 'fail', 'off']);
export type OpsStatus = z.infer<typeof OpsStatus>;
export const OPS_STATUS_LABELS: Record<OpsStatus, string> = {
  ok: 'в порядке',
  warn: 'внимание',
  fail: 'сбой',
  off: 'не настроено',
};

/** Отчёт со скрипта на сервере (копия готова, восстановление проверено, шифрование включено) */
export const OpsReportInput = z.object({
  kind: ReportedCheckKey,
  ok: z.boolean(),
  /** Короткое описание без паролей и адресов: «дамп 412 МБ, проверен pg_restore --list» */
  detail: z.string().trim().max(300).optional(),
  sizeBytes: z.number().int().nonnegative().optional(),
  durationSec: z.number().int().nonnegative().optional(),
});
export type OpsReportInput = z.infer<typeof OpsReportInput>;

export const OpsCheckView = z.object({
  key: OpsCheckKey,
  title: z.string(),
  status: OpsStatus,
  detail: z.string().nullable(),
  checkedAt: z.string().nullable(),
  lastOkAt: z.string().nullable(),
  sizeBytes: z.number().int().nullable(),
});
export type OpsCheckView = z.infer<typeof OpsCheckView>;

export const OpsEventView = z.object({
  id: z.uuid(),
  at: z.string(),
  severity: z.enum(['info', 'warn', 'error']),
  title: z.string(),
  details: z.string().nullable(),
});
export type OpsEventView = z.infer<typeof OpsEventView>;

/** Канал уведомлений на странице состояния: настроен ли, ответил ли сервер при последней проверке */
export const ChannelState = z.object({
  configured: z.boolean(),
  status: OpsStatus,
  detail: z.string().nullable(),
  checkedAt: z.string().nullable(),
});
export type ChannelState = z.infer<typeof ChannelState>;

/** «Состояние системы» (АД-06): для администратора компании — каналы и доставка; для владельца портала ещё копии, реплика, мониторинг */
export const SystemStatus = z.object({
  generatedAt: z.string(),
  commit: z.string(),
  ready: z.object({ db: z.boolean(), redis: z.boolean(), storage: z.boolean() }),
  tls: z.object({ publicUrlHttps: z.boolean(), cookieSecure: z.boolean() }),
  channels: z.object({
    email: ChannelState.extend({ from: z.string().nullable() }),
    telegram: ChannelState.extend({ botUsername: z.string().nullable() }),
    webhook: ChannelState,
  }),
  delivery: z.object({
    /** За последние сутки по сотрудникам этой компании */
    sent: z.number().int(),
    failed: z.number().int(),
    skipped: z.number().int(),
    /** Недоставленное за 7 дней — первым */
    recentFailed: z.array(DeliveryView),
  }),
  /** Только владельцу портала: инфраструктура всех компаний */
  platform: z
    .object({
      checks: z.array(OpsCheckView),
      events: z.array(OpsEventView),
      outbox: z.object({ pending: z.number().int(), dead: z.number().int() }),
      /** Какие проверки обязательны (OPS_EXPECT) — по ним просрочка считается сбоем */
      expected: z.array(OpsCheckKey),
    })
    .nullable(),
});
export type SystemStatus = z.infer<typeof SystemStatus>;

export const ChannelPingResult = z.object({
  results: z.array(
    z.object({
      channel: z.enum(['email', 'telegram']),
      status: z.enum(['sent', 'failed', 'pending']),
      error: z.string().nullable(),
    }),
  ),
});
export type ChannelPingResult = z.infer<typeof ChannelPingResult>;

/** Ответ проверки для внешнего наблюдателя (infra/kz/scripts/probe.sh): одно слово и список проблем */
export const OpsProbe = z.object({
  ok: z.boolean(),
  problems: z.array(z.string()),
  commit: z.string(),
});
export type OpsProbe = z.infer<typeof OpsProbe>;
